import { ReactComponent as CloseIcon } from '../../../assets/icons/x-icon.svg';
import { ReactComponent as UploadCloudIcon } from '../../../assets/icons/upload-cloud-icon.svg';
import { ReactComponent as ExcelIcon } from '../../../assets/icons/excel-icon.svg';
import { ReactComponent as CloseCircleIcon } from '../../../assets/icons/close-circle-icon.svg';
import { ReactComponent as LoadingIcon } from '../../../assets/icons/upload-loading-icon.svg';
import { ReactComponent as TickCircleIcon } from '../../../assets/icons/tick-circle-icon.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/trash-icon.svg';
import React, { useState, useRef } from 'react';
import './UploadExcelModal.css';
import { assortmentService } from '../../../services/assortmentService.js';
import { parseApiError } from '../../../utils/parseErrorMessage.js';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.csv'];

function isValidFile(file) {
    return file && ACCEPTED_EXTENSIONS.some(ext => file.name.endsWith(ext));
}

function useFileUpload(uploadFn, onUploaded) {
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [uploadedSize, setUploadedSize] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (!isValidFile(selectedFile)) return;

        setFile(selectedFile);
        setUploading(true);
        setError(null);
        setProgress(0);
        setUploadedSize(0);

        uploadFn(selectedFile, (progressEvent) => {
            const percent = Math.min(
                Math.round((progressEvent.loaded / progressEvent.total) * 95),
                95
            );
            setProgress(percent);
            setUploadedSize(progressEvent.loaded);
        })
            .then((response) => {
                if (response?.rows_inserted || response?.batch_id) {
                    onUploaded(true);
                    setError(null);
                }
            })
            .catch((err) => {
                const errorMessage = parseApiError(err);
                setError(errorMessage || 'Неизвестная ошибка');
                onUploaded(false);
            })
            .finally(() => {
                setProgress(100);
                setUploadedSize(selectedFile.size);
                setUploading(false);
            });
    };

    const handleCancel = () => {
        setFile(null);
        setProgress(0);
        setUploadedSize(0);
        setUploading(false);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
        onUploaded(false);
    };

    return { file, progress, uploadedSize, uploading, error, inputRef, handleFileSelect, handleCancel };
}

function formatFileSize(bytes) {
    return bytes < 1024 ? `${bytes.toFixed(0)} B` : `${(bytes / 1024).toFixed(0)} KB`;
}

function UploadItem({ label, accept, uploadState }) {
    const { file, progress, uploadedSize, uploading, error, inputRef, handleFileSelect, handleCancel } = uploadState;

    return (
        <div className="upload-item">
            <div className="upload-select">
                <span className="upload-item__text">{label}</span>
                {!file && (
                    <>
                        <input
                            ref={inputRef}
                            type="file"
                            accept={accept}
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <button onClick={() => inputRef.current?.click()} className="primary-button">
                            <span>Загрузить</span>
                        </button>
                    </>
                )}
            </div>

            {file && (
                <div className="upload-item-progress-container">
                    <div className="upload-item-progress">
                        <div className="upload-item-progress-main-container">
                            <div className="upload-item-progress-main-container">
                                <ExcelIcon size={40} />
                                <div className="upload-item-progress-info">
                                    <div className="upload-item-filename">{file.name}</div>
                                    <div className="upload-item-progress-caption">
                                        <span className="upload-item-progress-size">
                                            {formatFileSize(uploadedSize)} of {formatFileSize(file.size)} •
                                        </span>
                                        <span className="upload-item-progress-text">
                                            {uploading && <LoadingIcon size={20} color="#7CC805" className="loading-icon spinning" />}
                                            {!error && !uploading && <TickCircleIcon width={20} color="#7CC805" />}
                                            {error && <CloseCircleIcon width={20} color='#DC1A00' />}
                                            {!error && (uploading ? 'Загружается...' : 'Загружено')}
                                            {error && 'Ошибка'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleCancel}
                                className='modal-header-close-button'
                                style={{ marginBottom: 'auto', padding: '0px' }}
                            >
                                {uploading
                                    ? <CloseCircleIcon size={24} color="#898989" />
                                    : <TrashIcon size={24} color="#898989" />
                                }
                            </button>
                        </div>

                        <div className="upload-item-progress-bar">
                            <div className="upload-item-progress-bar-fill" style={{ width: `${progress}%` }} />
                        </div>
                        {error && <div style={{ color: '#DC1A00' }}>{error}</div>}
                    </div>
                </div>
            )}
        </div>
    );
}

const UploadExcelModal = ({
    closeModal,
    onAssortmentUploaded,
    onBranchUploaded,
    onSalesUploaded,
    onPriceUploaded,
}) => {
    const assortmentUpload = useFileUpload(assortmentService.uploadAssortmentExcel, onAssortmentUploaded);
    const branchUpload = useFileUpload(assortmentService.uploadBranchesExcel, onBranchUploaded);
    const salesUpload = useFileUpload(assortmentService.uploadSalesExcel, onSalesUploaded);
    const priceUpload = useFileUpload(assortmentService.uploadPriceListExcel, onPriceUploaded);

    const accept = ".xlsx,.xls,.xlsm,.csv";

    return (
        <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-container" style={{ minWidth: '40%', width: '45%' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header" style={{ justifyContent: 'flex-end' }}>
                    <button
                        onClick={closeModal}
                        className='modal-header-close-button'
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#4b5563')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#9ca3af')}
                    >
                        <CloseIcon size={24} />
                    </button>
                </div>

                <div className="upload-text-container">
                    <UploadCloudIcon color="#7CC805" width={64} height={64} />
                    <div className="upload-text-title">Загрузить Excel файл</div>
                    <div className="upload-text-description">Выберите файл Excel до 50 МБ</div>
                </div>

                <div className="upload-group">
                    <UploadItem label="Таблица с ассортиментом" accept={accept} uploadState={assortmentUpload} />
                    <UploadItem label="Таблица по филиалам" accept={accept} uploadState={branchUpload} />
                    <UploadItem label="Таблица по продажам" accept={accept} uploadState={salesUpload} />
                    <UploadItem label="Таблица по Прайс-листу" accept={accept} uploadState={priceUpload} />
                </div>

                <div className="upload-text-description">
                    Для правильного отображения информации на таблице Excel файл должен определенной формы.{' '}
                    <span style={{ color: '#7CC805', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => assortmentService.downloadExcelTemplatesZip()}>Скачать шаблоны</span>
                </div>
            </div>
        </div>
    );
};

export default UploadExcelModal;
